'use client';
import { createClient } from '@/utils/supabase/client';
import { Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import Logo from '../app/Logo';
import ChatWidgetMessages from './ChatWidgetMessages';

const DemoBanner = () => {
  return (
    <div className='p-4 bg-blue-100 text-slate-700 text-xs flex flex-row align-middle items-center'>
      <Info size={12} className='mr-2' />
      Try interacting with our chat bot demo below
    </div>
  );
};

export default function ChatWidget({
  teamId,
  demo = false,
  showHeader = true,
}: {
  teamId: number;
  demo?: boolean;
  showHeader?: boolean;
}) {
  const supabase = createClient();
  const [widgetTheme, setWidgetTheme] = useState<string>('');
  const [conversations, setConversations] = useState<WebChatConversation[]>([]);
  const [updateTrigger, setUpdateTrigger] = useState(false);
  const [enabled, setEnabled] = useState(true);

  // Get user id from local storage

  const [userId, setUserId] = useState<string | null>(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    return localStorage.getItem('avoca_chat_user_id');
  });

  // Get all conversations for the team from Supabase

  useEffect(() => {
    if (!teamId || !userId) {
      return;
    }

    const getEnabled = async () => {
      const { data } = await supabase
        .from('web_chat_config')
        .select('enabled')
        .eq('team_id', teamId)
        .single();

      if (!data?.enabled) {
        setEnabled(false);
      }
    };
    console.log('calling getEnabled');
    getEnabled();

    const fetchConversations = async () => {
      const { data: conversations, error } = await supabase
        .from('web_chat_conversations')
        .select('*')
        .eq('team_id', teamId)
        .eq('open', true)
        .eq('to', userId);

      if (error || !conversations) {
        console.error('Error fetching conversations:', error);
        return;
      }

      setConversations(conversations);
      console.log('Conversations:', conversations);
    };

    fetchConversations();
  }, [teamId, supabase, userId, updateTrigger]);

  const startConvoHandler = async (conversationId: string) => {
    try {
      const res = await fetch(
        `/api/web-chat/start-conversation?conversationId=${encodeURIComponent(
          conversationId
        )}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!res.ok) {
        throw new Error('API response was not ok.');
      }

      setUpdateTrigger(!updateTrigger); // Trigger a re-fetch of conversations
    } catch (err) {
      console.error('Error starting conversation:', err);
    }
  };

  const handleStartConversation = async () => {
    if (!teamId) {
      return;
    }

    const { data: conversations, error } = await supabase
      .from('web_chat_conversations')
      .insert({
        team_id: teamId,
        open: true,
        timezone_offset: new Date().getTimezoneOffset(),
        ...(userId && { to: userId }),
      })
      .select('*');

    if (error || !conversations) {
      console.error('Error starting conversation:', error);
      return;
    }

    if (!userId) {
      const toId = conversations[0].to;
      localStorage.setItem('avoca_chat_user_id', toId);
      setUserId(toId);
    }

    startConvoHandler(conversations[0].id);
  };

  useEffect(() => {
    const getConfig = async () => {
      const { data } = await supabase
        .from('web_chat_config')
        .select('widget_theme')
        .eq('team_id', teamId)
        .single<{ widget_theme: string | null }>();

      setWidgetTheme(data?.widget_theme || '');
    };
    getConfig();
  }, [teamId, supabase]);

  // Hide chat if it's disabled
  if (!enabled) {
    return null;
  }

  // If there are no conversations, show the widget with a button to start a conversation
  if (!conversations.length) {
    return (
      <div className='flex flex-col relative bg-white min-h-[500px] justify-between'>
        {showHeader && <p className='p-4 font-medium'>Start a conversation</p>}
        {demo && <DemoBanner />}
        <div className='flex flex-col basis-100 grow justify-between divide-gray-300/50 border-t border-gray-300/50'>
          <div className='space-y-6 py-8 text-base leading-7 text-gray-600 overflow-y-auto'>
            <ul className='space-y-4 px-4'>
              <li className='flex items-center'>
                <p className='bg-gray-100 p-4 rounded-md text-left'>
                  Thank you for reaching out. How may I help you today?
                </p>
              </li>
            </ul>
          </div>
          <div className='flex flex-end mx-4 py-4'>
            <button
              onClick={handleStartConversation}
              className={`rounded-md p-4 text-base font-semibold leading-7 text-white ${
                widgetTheme ? '' : 'bg-brandBlue hover:bg-blue-600'
              } w-full text-left hover:brightness-90 transition-all`}
              style={widgetTheme ? { backgroundColor: widgetTheme } : undefined}
            >
              Start a new conversation
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If there are conversations, show the widget with the messages

  const conversation = conversations[0];

  const handleCloseConversation = async () => {
    if (!teamId || !userId) {
      return;
    }

    const { data: conversations, error } = await supabase
      .from('web_chat_conversations')
      .update({ open: false })
      .eq('team_id', teamId)
      .eq('open', true)
      .eq('to', userId)
      .select('*');

    if (error || !conversations) {
      console.error('Error closing conversation:', error);
      return;
    }

    setUpdateTrigger(!updateTrigger); // Trigger a re-fetch of conversations
  };

  return (
    <div className='relative bg-white'>
      <ChatWidgetHeader onClose={handleCloseConversation} />
      {demo && <DemoBanner />}
      <ChatWidgetMessages
        conversationId={conversation.id}
        completed={
          conversation.open === false || conversation.mode === 'COMPLETED'
        }
        demo={demo}
        widgetTheme={widgetTheme}
      />
      <div className='flex flex-row place-content-center align-middle items-center'>
        <div className='text-xs text-gray-400'>
          Powered by{' '}
          <a href='/demo' className='underline underline-offset-4'>
            Avoca
          </a>
        </div>
        <Logo logoClassName='scale-[0.40] -ml-1' variant='logo' />
      </div>
    </div>
  );
}

const ChatWidgetHeader = ({ onClose }: { onClose: () => void }) => {
  return (
    <div className='flex items-center justify-between p-4 bg-white'>
      <p className='font-medium'>Chat with us</p>
      <button
        onClick={onClose}
        className='rounded-md p-2 font-semibold leading-4 text-black bg-gray-200'
      >
        Restart
      </button>
    </div>
  );
};
